import { EMRdetailsProps } from "@/api_framework/api_modals/EMRs";
import EMRCalendar from "@/app/components/ui/Calendar/EMRCalendar";
import MandatoryLabel from "@/app/components/ui/Reusable/MandatoryLabel";
import { useUpdateTempEMRAPI } from "@/app/hooks/api_hooks/emr/useUpdateTempEMRAPI";
import { useModalActions } from "@/app/hooks/ui_hooks/useModalActions";
import { useModalState } from "@/app/hooks/ui_hooks/useModalState";
import { useUtils } from "@/app/hooks/useUtils";
import { format, subMonths } from "date-fns";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { FiCalendar } from "react-icons/fi";
import { BLUE7_BORIVALI_CLINIC_ID } from "@/constants";

const BasicCheckupComponent = ({ emrDetails, setEmrDetails }: EMRdetailsProps) => {
  const basic_checkup = emrDetails?.basic_checkup;
  // Blue 7 Borivali clinic: deworming is not mandatory.
  const isBlue7BorivaliClinic = emrDetails?.visited_clinic_id === BLUE7_BORIVALI_CLINIC_ID;
  const isDewormingMandatory =
    emrDetails?.emr_type === "Post Grooming Consultation" && !isBlue7BorivaliClinic;
  const { getCurrentDate } = useUtils();
  const sixMonthsAgoDate = format(subMonths(new Date(), 6), "yyyy-MM-dd");

  const params = new URLSearchParams(location.search);
  const emr_id = params.get("emr_id");
  const emr_status = params.get("emr_status");

  const { veticDateFormat } = useUtils();
  const { execute: updateEmrAPI } = useUpdateTempEMRAPI();
  const { setVisible: showVaccinationModal, setModalData: setVaccinationModalData } =
    useModalActions("VACCINATION_ENTER_DATA_MODAL");

  const openVaccinationModal = () => {
    setVaccinationModalData({ isEMR: true, basicCheckup: emrDetails?.basic_checkup });
    showVaccinationModal();
  };

  const { savedEmr } = useModalState("VACCINATION_ENTER_DATA_MODAL");

  useEffect(() => {
    if (savedEmr) {
      setEmrDetails(savedEmr);
      setVaccinationModalData({});
    }
  }, [savedEmr]);

  const formHook = useForm<any>({
    mode: "onChange",
  });

  const handelUpdateData = (section, date) => {
    let constructedBody = null;
    if (section === "ectoparasiticide_date") {
      constructedBody = {
        basic_checkup: {
          ectoparasiticide_cover: {
            status: emrDetails?.basic_checkup?.ectoparasiticide_cover?.status,
            remarks: emrDetails?.basic_checkup?.ectoparasiticide_cover?.remarks,
            new_date: date,
          },
          deworming: {
            status: emrDetails?.basic_checkup?.deworming?.status,
            remarks: emrDetails?.basic_checkup?.deworming?.remarks,
            new_date: emrDetails?.basic_checkup?.deworming?.new_date,
          },
          vaccination: {
            status: emrDetails?.basic_checkup?.vaccination?.status,
            remarks: emrDetails?.basic_checkup?.vaccination?.remarks,
            new_date: emrDetails?.basic_checkup?.vaccination?.new_date,
          },
        },
        emr_status: emr_status,
      };
    } else {
      constructedBody = {
        basic_checkup: {
          ectoparasiticide_cover: {
            status: emrDetails?.basic_checkup?.ectoparasiticide_cover?.status,
            remarks: emrDetails?.basic_checkup?.ectoparasiticide_cover?.remarks,
            new_date: emrDetails?.basic_checkup?.ectoparasiticide_cover?.new_date,
          },
          deworming: {
            status: emrDetails?.basic_checkup?.deworming?.status,
            remarks: emrDetails?.basic_checkup?.deworming?.remarks,
            new_date: date,
          },
          vaccination: {
            status: emrDetails?.basic_checkup?.vaccination?.status,
            remarks: emrDetails?.basic_checkup?.vaccination?.remarks,
            new_date: emrDetails?.basic_checkup?.vaccination?.new_date,
          },
        },
        emr_status: emr_status,
      };
    }
    updateEmrAPI(emr_id, constructedBody).then(({ status, message }) => {
      if (status) {
        setEmrDetails(message);
      }
    });
  };

  const isFirstLoad = useRef(true);

  useEffect(() => {
    if (emrDetails?.basic_checkup) {
      isFirstLoad.current = true;
      formHook.setValue("ectoparasiticide_date", emrDetails?.basic_checkup?.ectoparasiticide_cover?.new_date);
      formHook.setValue("deworming_date", emrDetails?.basic_checkup?.deworming_date?.new_date);
      setTimeout(() => {
        isFirstLoad.current = false;
      }, 0);
    }
  }, [emrDetails, formHook]);

  const ReadMore: React.FC<{ text?: string; maxLength?: number }> = ({ text = "", maxLength = 100 }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    if (text.length <= maxLength) return <p className="text-sm text-[#6C727F] tracking-[1%]">{text}</p>;

    return (
      <p className="text-sm text-[#6C727F] tracking-[1%]">
        {isExpanded ? text : `${text.slice(0, maxLength)}...`}
        <button type="button" onClick={() => setIsExpanded(!isExpanded)} className="text-xs text-[#0080FF] ml-1 font-medium">
          {isExpanded ? "Read Less" : "Read More"}
        </button>
      </p>
    );
  };
  const hideCalendarStatuses = ["uptodate", "upcoming","recorded"];
  const isCalendarHidden = (status?: string) =>
    hideCalendarStatuses.includes((status ?? "").replace(/\s+/g, "").toLowerCase());
  const statusStyles = {
    "At Risk": "bg-[#FFF6F6] text-[#ED0101]",
    "Missing Data": "bg-[#FFF9F6] text-[#FF540B]",
    "Up to Date": "bg-[#F6FFF6] text-[#00873C]",
  };

  return (
    <div className=" w-full py-3 bg-white p-3 rounded-[10px] mt-3">
      <h3 className="text-[#656565] text-sm font-medium tracking-tight42 px-2">
        Basic Checkup <MandatoryLabel label="*" className="text-xs text-[#ED0101] " />{" "}
      </h3>
      <div className="bg-[#F9FAFB] w-full rounded-xl p-2 flex flex-col sm:flex-row gap-3 sm:items-stretch mt-2">
        <div className="w-full sm:w-1/3">
          <h3 className="text-[#000000] text-sm font-medium tracking-tight42">Ectoparasiticide Cover</h3>
          <div className="bg-[#FFFFFF] rounded-xl p-[10px] border border-[#E2E2E2] mt-2 ">
            <div className="flex items-center justify-between gap-2">
              <div className="flex min-w-0 flex-wrap items-center gap-2">
                <button
                  type="button"
                  className={`${statusStyles[basic_checkup?.ectoparasiticide_cover?.status]} p-1 border-transparent rounded-full font-medium text-xs sm:text-sm tracking-[1%] px-4 sm:px-5 whitespace-nowrap`}
                >
                  {basic_checkup?.ectoparasiticide_cover?.status}{" "}
                </button>
                {veticDateFormat(basic_checkup?.ectoparasiticide_cover?.new_date) &&
                  <button type="button" className="bg-[#F0F0F0] text-[#656565] rounded-full px-3 py-1.5 flex items-center gap-1 justify-center font-medium text-xs">
                    <span>
                    </span>
                    <span>{veticDateFormat(basic_checkup?.ectoparasiticide_cover?.new_date)}</span>
                  </button>}
              </div>

              <button type="button" className="shrink-0">
                {!isCalendarHidden(basic_checkup?.ectoparasiticide_cover?.status) && (
                  <EMRCalendar
                    allowPastDaySelection={true}
                    disabledFutureDateYearMonthSelection={true}
                    disableDatesAfterDate={getCurrentDate()}
                    disableDatesBeforeDate={sixMonthsAgoDate}
                    errorMessages={[{ message: "Date is required", type: "required" }]}
                    register={formHook.register("ectoparasiticide_date", {
                      required: true,
                    })}
                    showTypedErrors={false}
                    showLeftNav={false}
                    fieldError={formHook.formState.errors.ectoparasiticide_date}
                    onSelectDate={(date) => {
                      formHook.setValue("ectoparasiticide_date", format(date, "yyyy-MM-dd"));
                      handelUpdateData("ectoparasiticide_date", format(date, "yyyy-MM-dd"));
                    }}
                    defaultSelectedDate={formHook.watch("ectoparasiticide_date")}
                    className=""
                  />
                )}
              </button>
            </div>

            <ReadMore text={basic_checkup?.ectoparasiticide_cover?.remarks} maxLength={100} />
          </div>
        </div>
        <div className="w-full sm:w-1/3">
          <h3 className="text-[#000000] text-sm font-medium tracking-tight42">
            Deworming
            {isDewormingMandatory && <span className="text-red-400"> *</span>}
          </h3>
          <div className="bg-[#FFFFFF] rounded-xl p-[10px] border border-[#E2E2E2] mt-2 ">
            <div className="flex items-center justify-between gap-2">
              <div className="flex min-w-0 flex-wrap items-center gap-2">
                <button
                  type="button"
                  className={`${statusStyles[basic_checkup?.deworming?.status]} p-1 border-transparent rounded-full font-medium text-xs sm:text-sm tracking-[1%] px-4 sm:px-5 whitespace-nowrap`}
                >
                  {basic_checkup?.deworming?.status}
                </button>
                {veticDateFormat(basic_checkup?.deworming?.new_date) &&
                  <button type="button" className="bg-[#F0F0F0] text-[#656565] rounded-full px-3 py-1.5 flex items-center gap-1 justify-center font-medium text-xs">
                    <span>{veticDateFormat(basic_checkup?.deworming?.new_date)}</span>
                  </button>}
              </div>
              <button type="button" className="shrink-0">
                {!isCalendarHidden(basic_checkup?.deworming?.status) && (
                  <EMRCalendar
                    allowPastDaySelection={true}
                    disabledFutureDateYearMonthSelection={true}
                    disableDatesAfterDate={getCurrentDate()}
                    disableDatesBeforeDate={sixMonthsAgoDate}
                    errorMessages={[{ message: "Date is required", type: "required" }]}
                    register={formHook.register("deworming_date", {
                      required: !isBlue7BorivaliClinic,
                    })}
                    showTypedErrors={false}
                    showLeftNav={false}
                    fieldError={formHook.formState.errors.deworming_date}
                    onSelectDate={(date) => {
                      formHook.setValue("deworming_date", format(date, "yyyy-MM-dd"));
                      handelUpdateData("deworming_date", format(date, "yyyy-MM-dd"));
                    }}
                    defaultSelectedDate={formHook.watch("deworming_date")}
                    className=""
                  />
                )}
              </button>
            </div>

            <ReadMore text={basic_checkup?.deworming?.remarks} maxLength={100} />
          </div>
        </div>
        <div className="w-full sm:w-1/3">
          <h3 className="text-[#000000] text-sm font-medium tracking-tight42">Vaccination</h3>
          <div className="group relative bg-[#FFFFFF] rounded-xl p-[10px] border border-[#E2E2E2] mt-2">
            {basic_checkup?.vaccination?.vaccination_name?.trim() && (
              <div className="pointer-events-none absolute bottom-full left-1/2 z-20 mb-2 hidden -translate-x-1/2 whitespace-nowrap rounded-lg bg-black px-3 py-1.5 text-xs font-medium text-white shadow-md group-hover:block">
                {basic_checkup?.vaccination?.vaccination_name}
              </div>
            )}
            <div className="flex items-center justify-between gap-2">
              <div className="flex min-w-0 flex-wrap items-center gap-2">
              <button
                type="button"
                className={`${statusStyles[basic_checkup?.vaccination?.status]} p-1 border-transparent rounded-full font-medium text-xs sm:text-sm tracking-[1%] px-4 sm:px-5 whitespace-nowrap`}
              >
                {basic_checkup?.vaccination?.status}
              </button>
              {veticDateFormat(basic_checkup?.vaccination?.new_date) && <button type="button" className="bg-[#F0F0F0] text-[#656565] rounded-full px-3 py-1.5 flex items-center gap-1 justify-center font-medium text-xs">
                <span>

                </span>
                <span>{veticDateFormat(basic_checkup?.vaccination?.new_date)}</span>
              </button>}
              </div>
              {!isCalendarHidden(basic_checkup?.vaccination?.status) && (
                <button type="button" className="shrink-0" onClick={openVaccinationModal}>
                  <FiCalendar className="w-6 h-6 font-medium cursor-pointer" />
                </button>
              )}
            </div>
            <ReadMore text={basic_checkup?.vaccination?.remarks} maxLength={100} />
          </div>
        </div>
      </div>
    </div>
  );
};
export default BasicCheckupComponent;
